import type { TFunction } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { Trans } from 'react-i18next';

import { resolveFlag } from '../../services/launchDarkly';
import type { DailyMissionStatus, UserDailyMission } from '../../stores/daily-missions/types';
import { MissionTypeEnum } from '../../stores/daily-missions/types';
import { isFreeSpinsBundleItem } from '../../stores/rewards/helpers';
import type { BundleItem, FreeSpinsPayload } from '../../stores/rewards/types';
import { CoinWithValue } from '../CoinWithValue/CoinWithValue';

import { DailyMissionTimer } from './components/DailyMissionTimer';
import classes from './DailyMissions.module.scss';
import type { DialogType } from './DailyMissionsDialog';

export function getDialogType(status: DailyMissionStatus): DialogType | null {
  switch (status) {
    case 'COMPLETED':
      return 'gift';
    case 'ACTIVE':
      return 'description';
    case 'UPCOMING':
      return 'upcoming';
    default:
      return null;
  }
}

// Currently return only one FRB item if exists
export function extractPackageBundleFRB(bundleItems: BundleItem[]) {
  const item =
    (bundleItems.reduce((acc, bundleItem) => {
      if (isFreeSpinsBundleItem(bundleItem)) {
        acc = bundleItem.bundlePayload;
      }

      return acc;
    }, {}) as FreeSpinsPayload) || {};

  if (isEmpty(item)) {
    return null;
  }
  return item;
}

export function getDialogTitle(
  type: DialogType,
  scCoin: number,
  gcCoin: number,
  frbBundlePayload: FreeSpinsPayload | null,
  t: TFunction,
) {
  const featureFlagTitle = resolveFlag('daily_mission_title', false);

  if (type === 'description') {
    return featureFlagTitle ? featureFlagTitle : t('dailyMissions.dailyMission');
  } else if (type === 'upcoming') {
    return t('dailyMissions.congratsLowerCase');
  } else {
    if (scCoin > 0 && gcCoin > 0 && !isEmpty(frbBundlePayload)) {
      return t('dailyMissions.royalWin');
    } else if (scCoin > 0 && gcCoin > 0 && isEmpty(frbBundlePayload)) {
      return t('dailyMissions.royal');
    } else if ((scCoin > 0 || gcCoin > 0) && isEmpty(frbBundlePayload)) {
      return t('dailyMissions.awesome');
    } else if (frbBundlePayload) {
      return t('dailyMissions.wellDone');
    } else {
      return t('dailyMissions.dailyMission');
    }
  }
}

export function getProgressBarTitle(missionType: MissionTypeEnum, coinType: string, t: TFunction) {
  switch (missionType) {
    case MissionTypeEnum.WIN_A_BIG_WIN:
      return t('dailyMissions.bigWins');
    case MissionTypeEnum.BET_SUM_OF:
      return `${coinType === 'GC' ? 'CC' : coinType} ${t('dailyMissions.amount')}`;
    case MissionTypeEnum.WIN_FREE_SPINS:
      return t('dailyMissions.freeSpins');
    case MissionTypeEnum.WIN_X_TIMES:
      return t('dailyMissions.wins');
    case MissionTypeEnum.WIN_X_TIMES_IN_A_ROW:
      return t('dailyMissions.winsInARow');
    case MissionTypeEnum.WIN_X_IN_ONE_SPIN:
      return t('dailyMissions.winXInOneSpin');
    case MissionTypeEnum.WIN_A_JACKPOT:
      return t('dailyMissions.jackpot');
    case MissionTypeEnum.SPIN_X_TIMES:
      return t('dailyMissions.spins');
    case MissionTypeEnum.WIN_SUM_OF:
      return `${coinType === 'GC' ? 'CC' : coinType} ${t('dailyMissions.wins')}`;
    default:
      return '';
  }
}

export function getButtonText(dialogType: DialogType, isSpecificGame: boolean, t: TFunction) {
  switch (dialogType) {
    case 'description':
      return isSpecificGame ? t('dailyMissions.playNow') : t('dailyMissions.awesome');
    case 'gift':
      return t('common.buttons.claimNowLowerCase');
    case 'upcoming':
      return t('dailyMissions.seeYou');
    default:
      return '';
  }
}

export const getStaticMessage = (currentMission: UserDailyMission, t: TFunction) => {
  if (currentMission.status === 'UPCOMING') {
    return (
      <div className={classes.upcomingBar}>
        <span> {t('bonus.nextIn')} </span>
        <span>
          <DailyMissionTimer />
        </span>
      </div>
    );
  } else if (
    ['ACTIVE', 'COMPLETED'].includes(currentMission.status) &&
    currentMission.missionType === MissionTypeEnum.WIN_X_IN_ONE_SPIN
  ) {
    return (
      <div data-testid="daily-mission-win-in-one-spin">
        <Trans
          components={{
            win: <span />,
            coin: <CoinWithValue coinSide="left" coinType={currentMission.coinType} />,
            rest: <span />,
          }}
          i18nKey="dailyMissions.winInOneSpinSentence"
          values={{ value: Number(currentMission.goal).toLocaleString() }}
        />
      </div>
    );
  } else {
    return undefined;
  }
};

export function getIntegerProgress(progress: number) {
  return Number(progress.toFixed(0)).toLocaleString();
}
